/**
 * §7.4b Ensemble Forecast. Runs N parametric draws around the catalog
 * central R0 + IFR, aggregates into per-day quantile bands via
 * ensembleQuantiles, and shows the 95% predictive interval as a
 * shaded band on the I and D compartments. Mirrors the Reich Lab
 * COVID-19 Forecast Hub pattern at small scale.
 */

import { useMemo, useState } from 'react';
import { findPathogen, listPathogens } from '@biomax/plugin-pathogen-database';
import { findRegion, listRegions } from '@biomax/plugin-region-database';
import { ensembleQuantiles, type EnsembleMember } from '@biomax/plugin-epidemiology';
import { Sparkline } from '../sparkline';
import { useScenario } from '../ScenarioContext';
import { simulateForPathogen, isVectorBornePathogen, pathogenIfr } from '../simRouting';
import { ValidationBacktest } from './ValidationBacktest';

/**
 * Top-level Forecast surface. CD R11 finding #14 (kill list) — Model
 * Validation (§7.4c) was a top-level peer of Ensemble (§7.4b); CD's read
 * was "no operator navigates to validation as a destination; they want
 * it as evidence INSIDE the forecast." Folded into a tab pair here.
 * Forecast (the ensemble fan) + Validation (backtest vs observed) are
 * the two modes of one noun. Keeps surface count at 35.
 */
type ForecastTab = 'forecast' | 'validation';

export function EnsembleForecast() {
  const [tab, setTab] = useState<ForecastTab>('forecast');
  return (
    <>
      <div role="tablist" aria-label="Forecast mode" style={{
        display: 'flex', gap: 0,
        marginBottom: 'var(--bm-space-3)',
        borderBottom: '1px solid var(--bm-border)',
      }}>
        {([
          { k: 'forecast' as const,   label: 'Forecast',   section: '§7.4b' },
          { k: 'validation' as const, label: 'Validation', section: '§7.4c' },
        ]).map(t => {
          const on = tab === t.k;
          return (
            <button
              key={t.k}
              role="tab"
              aria-selected={on}
              onClick={() => setTab(t.k)}
              style={{
                padding: '10px 16px',
                background: on ? 'var(--bm-bg)' : 'transparent',
                border: 'none',
                borderBottom: `2px solid ${on ? 'var(--bm-accent, #6ea3ff)' : 'transparent'}`,
                fontFamily: 'var(--bm-font-mono)', fontSize: 11.5, fontWeight: 600,
                color: on ? 'var(--bm-text)' : 'var(--bm-text-subtle)',
                cursor: 'pointer',
              }}
            >
              <span style={{ opacity: 0.7, marginRight: 6 }}>{t.section}</span>
              {t.label}
            </button>
          );
        })}
      </div>
      {tab === 'forecast'   && <EnsembleForecastBody />}
      {tab === 'validation' && <ValidationBacktest />}
    </>
  );
}

function EnsembleForecastBody() {
  const pathogens = listPathogens();
  const regions = listRegions();

  const { pathogenId, regionId, setPathogenId, setRegionId } = useScenario();
  const [memberCount, setMemberCount] = useState<number>(20);
  const [r0Spread, setR0Spread] = useState<number>(25);

  const view = useMemo(() => {
    const pathogen = findPathogen(pathogenId);
    const region = findRegion(regionId);
    if (!pathogen || !region) return null;

    const r0Central = pathogen.R0.value;
    const ifrCentral = pathogenIfr(pathogen);
    const N = region.population.total;
    const duration = 180;
    const vectorBorne = isVectorBornePathogen(pathogen);

    // Deterministic PRNG for ensemble draws.
    let seed = 314159;
    const rand = () => { seed = (seed * 1664525 + 1013904223) >>> 0; return seed / 0x100000000; };

    const members: EnsembleMember[] = [];
    for (let i = 0; i < memberCount; i++) {
      const r0Mult = 1 + ((rand() - 0.5) * 2 * r0Spread) / 100;
      const ifrMult = 1 + ((rand() - 0.5) * 1.0);
      const r0 = Math.max(0.1, r0Central * r0Mult);
      const ifr = Math.max(1e-5, ifrCentral * ifrMult);
      // Routes vector-borne pathogens through the host-vector model automatically.
      const sim = simulateForPathogen(pathogen, { N, I0: 100, r0, ifr, durationDays: duration, dtDays: 1 });
      members.push({ label: `R0=${r0.toFixed(2)} IFR=${ifr.toExponential(1)}`, result: sim });
    }

    const qI = ensembleQuantiles(members, 'I');
    const qD = ensembleQuantiles(members, 'D');

    return { pathogen, region, members, qI, qD, vectorBorne };
  }, [pathogenId, regionId, memberCount, r0Spread]);

  if (!view) return <div>Loading…</div>;

  const peakIMedian = Math.max(...view.qI.median);
  const peakIDay = view.qI.median.indexOf(peakIMedian);
  const finalDMedian = view.qD.median.at(-1)!;
  const finalDLow = view.qD.q025.at(-1)!;
  const finalDHigh = view.qD.q975.at(-1)!;

  return (
    <div className="surface">
      <div className="surface__header">
        <span className="surface__sec">§7.4b</span>
        <h1 className="surface__title">Ensemble Forecast</h1>
      </div>
      <div className="surface__sub">
        {view.members.length}-member parametric ensemble · R0 ± {r0Spread}% spread ·
        per-day quantile aggregation · Reich Lab Forecast Hub pattern
        {view.vectorBorne && <> · <strong style={{ color: 'var(--bm-accent)' }}>host-vector model</strong> (Ross-Macdonald SEIR-SEI, EIP delay)</>}
      </div>

      <div className="panel">
        <div className="panel__head"><span className="panel__title">Ensemble configuration</span></div>
        <div className="simctl">
          <label className="simctl__field">
            <span className="simctl__label">Pathogen</span>
            <select value={pathogenId} onChange={e => setPathogenId(e.target.value)}>
              {pathogens.map(p => <option key={p.id} value={p.id}>{p.names.common}</option>)}
            </select>
          </label>
          <label className="simctl__field">
            <span className="simctl__label">Region</span>
            <select value={regionId} onChange={e => setRegionId(e.target.value)}>
              {regions
                .slice()
                .sort((a, b) => a.names.english.localeCompare(b.names.english))
                .map(r => <option key={r.id} value={r.id}>{r.names.english}</option>)}
            </select>
          </label>
          <label className="simctl__field">
            <span className="simctl__label">Members · {memberCount}</span>
            <input type="range" min="5" max="50" step="1" value={memberCount}
              onChange={e => setMemberCount(parseInt(e.target.value, 10))} />
          </label>
          <label className="simctl__field">
            <span className="simctl__label">R0 spread · ±{r0Spread}%</span>
            <input type="range" min="5" max="50" step="1" value={r0Spread}
              onChange={e => setR0Spread(parseInt(e.target.value, 10))} />
          </label>
        </div>
      </div>

      <div className="kpis">
        <div className="kpi">
          <div className="kpi__label">Median deaths</div>
          <div className="kpi__value kpi__value--danger">{Math.round(finalDMedian).toLocaleString()}</div>
          <div className="kpi__sub">[{Math.round(finalDLow).toLocaleString()}-{Math.round(finalDHigh).toLocaleString()}] 95% PI</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">Peak I (median)</div>
          <div className="kpi__value kpi__value--signal">{(peakIMedian / 1e6).toFixed(2)}M</div>
          <div className="kpi__sub">day {peakIDay}</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">Spread ratio (D, P97.5/P2.5)</div>
          <div className="kpi__value">{(finalDHigh / Math.max(1, finalDLow)).toFixed(1)}×</div>
          <div className="kpi__sub">tighter = more confident forecast</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">Members</div>
          <div className="kpi__value">{view.members.length}</div>
          <div className="kpi__sub">parametric draws</div>
        </div>
      </div>

      <div className="panel">
        <div className="panel__head">
          <span className="panel__title">Infectious compartment · ensemble quantile bands</span>
          <span className="panel__meta">95% (q025-q975), 50% (q25-q75), median</span>
        </div>
        <Sparkline
          values={view.qI.median}
          bands={[
            { low: view.qI.q025, high: view.qI.q975, opacity: 0.12 },
            { low: view.qI.q25, high: view.qI.q75, opacity: 0.24 },
          ]}
          label="median · 50% + 95% bands"
          stroke="var(--bm-danger)"
          height={240}
          interactive
          xLabel={i => `day ${i}`}
        />
      </div>

      <div className="panel">
        <div className="panel__head">
          <span className="panel__title">Cumulative deaths · ensemble quantile bands</span>
        </div>
        <Sparkline
          values={view.qD.median}
          bands={[{ low: view.qD.q025, high: view.qD.q975, opacity: 0.16 }]}
          label="median · 95% band"
          stroke="var(--bm-danger)"
          height={240}
          interactive
          xLabel={i => `day ${i}`}
        />
      </div>

      <div className="panel">
        <div className="panel__head">
          <span className="panel__title">Why this matters</span>
        </div>
        <div style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--bm-text)' }}>
          A single deterministic projection over-states confidence. A {view.members.length}-member
          ensemble around the catalog R0 reveals the predictive interval: deaths span{' '}
          <strong>{(finalDHigh / Math.max(1, finalDLow)).toFixed(1)}×</strong> between the 2.5th
          and 97.5th percentiles. This pattern matches the Reich Lab COVID-19 Forecast Hub,
          where 50+ independent models are aggregated weekly. v0.3 adds proper CRPS scoring
          + retrospective backtesting against historical case data.
        </div>
      </div>
    </div>
  );
}
